/**
 * UTM Builder Page — Artillery UTM SOP
 *
 * Tailored to the Artillery UTM Standard Operating Procedure:
 * - utm_medium: "paid" or "organic" only
 * - utm_source: platform (instagram, youtube, tiktok, facebook, email, link+in+bio)
 * - utm_campaign: campaign identifier (e.g. webinar-jul2026, q3-launch)
 * - utm_content: specific ad ID or content piece name (e.g. reel-001, ad-id-12345)
 * - utm_term: NOT USED (removed per SOP)
 */

import { useState, useCallback, useEffect } from "react";
import { PageHeader } from "../components";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";

// ─── Icons ────────────────────────────────────────────────────────────────────

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const SaveIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const ListIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const BookmarkIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

// ─── Types ─────────────────────────────────────────────────────────────────────

interface UTMParams {
  url: string;
  source: string;
  medium: string;
  campaign: string;
  content: string;
}

interface SavedCampaign {
  _id: string;
  campaignName: string;
  baseUrl: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  generatedUrl: string;
  createdAt: string;
}

interface Preset {
  id: string;
  name: string;
  source: string;
  medium: string;
  campaign: string;
  content: string;
}

// ─── SOP Data ─────────────────────────────────────────────────────────────────

const SOURCE_OPTIONS = [
  { label: "Instagram", value: "instagram" },
  { label: "YouTube", value: "youtube" },
  { label: "TikTok", value: "tiktok" },
  { label: "Facebook", value: "facebook" },
  { label: "Email", value: "email" },
  { label: "Link in Bio", value: "link+in+bio" },
];

const MEDIUM_OPTIONS: Array<{ label: string; value: string; desc: string }> = [
  { label: "paid", value: "paid", desc: "Paid ads" },
  { label: "organic", value: "organic", desc: "Organic posts / content" },
];

const SOURCE_MEDIUM_SCENARIOS = [
  { scenario: "Paid ad on Instagram",              source: "instagram",    medium: "paid" },
  { scenario: "Organic post on Instagram",         source: "instagram",    medium: "organic" },
  { scenario: "Paid ad on YouTube",                source: "youtube",      medium: "paid" },
  { scenario: "YouTube video / description link",  source: "youtube",      medium: "organic" },
  { scenario: "Paid ad on TikTok",                 source: "tiktok",       medium: "paid" },
  { scenario: "Organic TikTok post",               source: "tiktok",       medium: "organic" },
  { scenario: "Facebook paid ad",                  source: "facebook",     medium: "paid" },
  { scenario: "Link in bio (any platform)",        source: "link+in+bio",  medium: "organic" },
  { scenario: "Email newsletter",                  source: "email",        medium: "organic" },
];

// ─── Presets Storage ──────────────────────────────────────────────────────────

const PRESETS_STORAGE_KEY = "utm_builder_presets_v2";

function loadPresets(): Preset[] {
  try {
    const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Preset[]) : [];
  } catch {
    return [];
  }
}

function savePresetsToStorage(presets: Preset[]) {
  localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(presets));
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function UTMBuilder() {
  const [params, setParams] = useState<UTMParams>({
    url: "",
    source: "",
    medium: "",
    campaign: "",
    content: "",
  });
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [savedCampaigns, setSavedCampaigns] = useState<SavedCampaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState<SavedCampaign | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<SavedCampaign | null>(null);

  const [presets, setPresets] = useState<Preset[]>([]);
  const [presetName, setPresetName] = useState("");
  const [presetDeleteId, setPresetDeleteId] = useState<string | null>(null);

  useEffect(() => { setPresets(loadPresets()); }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await fetch("/api/utm-campaigns");
      const result = await response.json();
      if (result.success) setSavedCampaigns(result.data);
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const generateURL = useCallback(() => {
    if (!params.url) return "";
    try {
      const url = new URL(params.url.startsWith("http") ? params.url : `https://${params.url}`);
      // SOP order: medium, source, campaign, content
      if (params.medium)   url.searchParams.set("utm_medium",   params.medium);
      if (params.source)   url.searchParams.set("utm_source",   params.source);
      if (params.campaign) url.searchParams.set("utm_campaign", params.campaign);
      if (params.content)  url.searchParams.set("utm_content",  params.content);
      return url.toString();
    } catch {
      return "";
    }
  }, [params]);

  const generatedURL = generateURL();

  const handleCopy = async () => {
    if (!generatedURL) return;
    try {
      await navigator.clipboard.writeText(generatedURL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleClear = () => {
    setParams({ url: "", source: "", medium: "", campaign: "", content: "" });
    setSaveSuccess(false);
    setSaveError(null);
    setEditingCampaign(null);
  };

  const handleSave = async () => {
    if (!params.url || !params.source || !params.medium || !params.campaign) {
      setSaveError("Please fill in all required fields (URL, Source, Medium, Campaign)");
      return;
    }
    if (!generatedURL) {
      setSaveError("Please enter a valid URL");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const isEditing = editingCampaign !== null;
      const apiUrl = isEditing ? `/api/utm-campaigns/${editingCampaign._id}` : "/api/utm-campaigns";
      const response = await fetch(apiUrl, {
        method: isEditing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaignName: params.campaign,
          baseUrl: params.url,
          utmSource: params.source,
          utmMedium: params.medium,
          utmCampaign: params.campaign,
          utmContent: params.content || undefined,
          generatedUrl: generatedURL,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save campaign");
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      await fetchCampaigns();
      if (!isEditing) handleClear();
      else setEditingCampaign(null);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (campaign: SavedCampaign) => {
    setEditingCampaign(campaign);
    setParams({
      url: campaign.baseUrl,
      source: campaign.utmSource,
      medium: campaign.utmMedium,
      campaign: campaign.utmCampaign,
      content: campaign.utmContent ?? "",
    });
    setSaveError(null);
    setSaveSuccess(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (campaign: SavedCampaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;
    setDeletingId(campaignToDelete._id);
    try {
      await fetch(`/api/utm-campaigns/${campaignToDelete._id}`, { method: "DELETE" });
      await fetchCampaigns();
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setDeletingId(null);
      setCampaignToDelete(null);
    }
  };

  const handleCancelEdit = () => { setEditingCampaign(null); handleClear(); };

  const updateParam = (key: keyof UTMParams, value: string) => {
    setParams(prev => ({ ...prev, [key]: value }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    const newPreset: Preset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      source: params.source,
      medium: params.medium,
      campaign: params.campaign,
      content: params.content,
    };
    const updated = [...presets, newPreset];
    setPresets(updated);
    savePresetsToStorage(updated);
    setPresetName("");
  };

  const handleLoadPreset = (preset: Preset) => {
    setParams(prev => ({
      ...prev,
      source: preset.source,
      medium: preset.medium,
      campaign: preset.campaign,
      content: preset.content,
    }));
  };

  const handleDeletePreset = (id: string) => {
    setPresetDeleteId(id);
    setTimeout(() => {
      const updated = presets.filter(p => p.id !== id);
      setPresets(updated);
      savePresetsToStorage(updated);
      setPresetDeleteId(null);
    }, 200);
  };

  const isFormValid = params.url && params.source && params.medium && params.campaign;

  const formatDate = (dateString: string) =>
    new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(dateString));

  const fieldClass = "w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/50";
  const labelClass = "block text-sm font-medium mb-1.5 text-foreground";
  const helperClass = "text-xs text-muted-foreground mt-1.5";

  return (
    <div className="space-y-6">
      <PageHeader title="UTM Builder" subtitle="Build UTM-tagged webinar funnel links — Artillery SOP" />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        {/* ── Form ─────────────────────────────────────────────────── */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
            <EditIcon />
            {editingCampaign ? "Edit UTM Link" : "Build UTM Link"}
          </h3>

          {editingCampaign && (
            <div className="mb-5 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-500 flex items-center justify-between">
              <span>Editing: {editingCampaign.campaignName}</span>
              <button type="button" onClick={handleCancelEdit} className="text-amber-500 hover:text-amber-400">Cancel</button>
            </div>
          )}

          <div className="space-y-5">

            {/* Base URL */}
            <div>
              <label className={labelClass}>
                Funnel URL <span className="text-destructive">*</span>
              </label>
              <input
                type="url"
                placeholder="https://yourfunnel.com/webinar"
                className={fieldClass}
                value={params.url}
                onChange={e => updateParam("url", e.target.value)}
              />
              <p className={helperClass}>The full landing page URL (e.g. <strong>https://yourfunnel.com/webinar</strong>)</p>
            </div>

            {/* utm_medium — segmented toggle: paid / organic only */}
            <div>
              <label className={labelClass}>
                utm_medium <span className="text-destructive">*</span>
              </label>
              <div className="flex gap-2">
                {MEDIUM_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateParam("medium", opt.value)}
                    className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-all ${
                      params.medium === opt.value
                        ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white shadow-sm"
                        : "border-border bg-background text-muted-foreground hover:border-[var(--brand-primary)]/50 hover:text-foreground"
                    }`}
                  >
                    {opt.label}
                    <span className={`block text-[10px] font-normal mt-0.5 ${params.medium === opt.value ? "text-white/70" : "text-muted-foreground/60"}`}>
                      {opt.desc}
                    </span>
                  </button>
                ))}
              </div>
              <p className={helperClass}>Only ever <strong>paid</strong> or <strong>organic</strong> — nothing else.</p>
            </div>

            {/* utm_source — text input + quick-select chips */}
            <div>
              <label className={labelClass}>
                utm_source <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                placeholder="instagram"
                className={fieldClass}
                value={params.source}
                onChange={e => updateParam("source", e.target.value.toLowerCase().replace(/ /g, ""))}
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {SOURCE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateParam("source", opt.value)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                      params.source === opt.value
                        ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white"
                        : "border-border bg-background text-muted-foreground hover:border-[var(--brand-primary)]/50 hover:text-foreground"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className={helperClass}>Where the click came from. Always lowercase, no spaces. For link in bio, use <strong>link+in+bio</strong>.</p>
            </div>

            {/* utm_campaign */}
            <div>
              <label className={labelClass}>
                utm_campaign <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                placeholder="webinar-jul2026"
                className={fieldClass}
                value={params.campaign}
                onChange={e => updateParam("campaign", e.target.value.toLowerCase().replace(/ /g, "-"))}
              />
              <p className={helperClass}>
                The campaign this belongs to — use <strong>topic-monthyear</strong> format (e.g. <strong>webinar-jul2026</strong>, <strong>q3-launch</strong>). Keep consistent across all links for the same campaign.
              </p>
            </div>

            {/* utm_content */}
            <div>
              <label className={labelClass}>utm_content</label>
              <input
                type="text"
                placeholder="reel-001"
                className={fieldClass}
                value={params.content}
                onChange={e => updateParam("content", e.target.value.toLowerCase().replace(/ /g, "-"))}
              />
              <p className={helperClass}>
                The specific ad or content piece — e.g. <strong>reel-001</strong>, <strong>ad-id-12345</strong>, <strong>yt-how-to-start</strong>. Hyphens instead of spaces.
              </p>
            </div>

            <hr className="border-border" />

            {/* Actions */}
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !isFormValid}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--brand-primary)] text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                {saving ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
                ) : saveSuccess ? (
                  <><CheckIcon />Saved!</>
                ) : (
                  <><SaveIcon />{editingCampaign ? "Update Campaign" : "Save Campaign"}</>
                )}
              </button>
              <button type="button" onClick={handleClear} className="text-sm text-muted-foreground hover:text-foreground">
                Clear All
              </button>
            </div>

            {saveError && <p className="text-sm text-destructive">{saveError}</p>}
          </div>
        </div>

        {/* ── Right Panel ──────────────────────────────────────────── */}
        <div className="space-y-6">

          {/* Generated URL */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold mb-4">Generated URL</h3>
            {generatedURL ? (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-4 break-all text-sm font-mono leading-relaxed">
                  {generatedURL}
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--brand-primary)] text-white font-medium hover:opacity-90 transition-opacity"
                >
                  {copied ? <><CheckIcon />Copied!</> : <><CopyIcon />Copy URL</>}
                </button>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-xl p-8 text-center">
                <p className="text-muted-foreground text-sm">Fill in the fields to generate your tagged URL</p>
              </div>
            )}
          </div>

          {/* Presets */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BookmarkIcon />
              Presets
            </h3>
            <p className="text-sm text-muted-foreground mb-4">Save the current UTM params as a preset to reuse quickly.</p>
            <div className="flex gap-2 mb-5">
              <input
                type="text"
                placeholder="e.g. Instagram Paid"
                className={`${fieldClass} flex-1`}
                value={presetName}
                onChange={e => setPresetName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSavePreset()}
              />
              <button
                type="button"
                onClick={handleSavePreset}
                disabled={!presetName.trim()}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-[var(--brand-primary)] text-[var(--brand-primary)] text-sm font-medium hover:bg-[var(--brand-primary)]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                <PlusIcon />
                Save
              </button>
            </div>
            {presets.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">No presets yet.</div>
            ) : (
              <div className="space-y-2">
                {presets.map(preset => (
                  <div
                    key={preset.id}
                    className={`flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors ${presetDeleteId === preset.id ? "opacity-40" : ""}`}
                  >
                    <div className="flex-1 min-w-0 mr-3">
                      <div className="font-medium text-sm">{preset.name}</div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {preset.medium && (
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${preset.medium === "paid" ? "bg-blue-500/10 text-blue-500" : "bg-green-500/10 text-green-500"}`}>
                            {preset.medium}
                          </span>
                        )}
                        {preset.source && (
                          <span className="px-1.5 py-0.5 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] rounded text-xs">{preset.source}</span>
                        )}
                        {preset.campaign && (
                          <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded text-xs">{preset.campaign}</span>
                        )}
                        {preset.content && (
                          <span className="px-1.5 py-0.5 bg-muted text-muted-foreground rounded text-xs">{preset.content}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleLoadPreset(preset)}
                        className="px-3 py-1.5 text-xs rounded-lg border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 transition-colors font-medium"
                      >
                        Load
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeletePreset(preset.id)}
                        className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors text-muted-foreground hover:text-red-500"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SOP Quick Reference */}
          <div className="bg-card rounded-xl border border-border p-6 space-y-5">
            <h3 className="text-lg font-semibold">Artillery UTM SOP — Quick Reference</h3>

            {/* Parameter definitions */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Parameters</p>
              <div className="space-y-2 text-sm">
                {[
                  { param: "utm_medium",   color: "text-blue-400",                  desc: "Traffic type — paid or organic only. Always lowercase." },
                  { param: "utm_source",   color: "text-[var(--brand-primary)]",    desc: "Platform the traffic came from (instagram, youtube, tiktok, facebook, email, link+in+bio)." },
                  { param: "utm_campaign", color: "text-purple-400",                desc: "Campaign identifier — use topic-monthyear format (e.g. webinar-jul2026). Keep consistent across all links in the same campaign." },
                  { param: "utm_content",  color: "text-muted-foreground",          desc: "Specific ad or content piece — e.g. reel-001, ad-id-12345, yt-video-title. Hyphens only, no spaces." },
                ].map(({ param, color, desc }) => (
                  <div key={param} className="flex gap-3">
                    <span className={`font-mono text-xs font-semibold whitespace-nowrap pt-0.5 ${color}`}>{param}</span>
                    <p className="text-muted-foreground text-xs leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <hr className="border-border" />

            {/* Source × Medium table */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Source × Medium Scenarios</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-2 pr-3 font-medium text-muted-foreground">Scenario</th>
                      <th className="pb-2 pr-3 font-medium text-muted-foreground">utm_source</th>
                      <th className="pb-2 font-medium text-muted-foreground">utm_medium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SOURCE_MEDIUM_SCENARIOS.map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-border/40 cursor-pointer hover:bg-muted/30 transition-colors"
                        title="Click to load into form"
                        onClick={() => {
                          updateParam("source", row.source);
                          updateParam("medium", row.medium);
                        }}
                      >
                        <td className="py-2 pr-3 text-muted-foreground">{row.scenario}</td>
                        <td className="py-2 pr-3">
                          <span className="font-mono text-[var(--brand-primary)]">{row.source}</span>
                        </td>
                        <td className="py-2">
                          <span className={`font-mono font-semibold ${row.medium === "paid" ? "text-blue-400" : "text-green-400"}`}>
                            {row.medium}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-muted-foreground/50 mt-2">Click any row to load source + medium into the form.</p>
              </div>
            </div>

            <hr className="border-border" />

            {/* Naming conventions */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Naming Conventions</p>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div className="flex gap-2"><span className="font-mono text-foreground w-28 flex-shrink-0">utm_medium</span><span>paid or organic — nothing else</span></div>
                <div className="flex gap-2"><span className="font-mono text-foreground w-28 flex-shrink-0">utm_source</span><span>lowercase platform name (instagram, youtube…)</span></div>
                <div className="flex gap-2"><span className="font-mono text-foreground w-28 flex-shrink-0">utm_campaign</span><span>topic-monthyear → webinar-jul2026</span></div>
                <div className="flex gap-2"><span className="font-mono text-foreground w-28 flex-shrink-0">utm_content</span><span>short-descriptor or ad ID → reel-002, ad-12345</span></div>
              </div>
            </div>

            <hr className="border-border" />

            {/* Pre-publish checklist */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Pre-Publish Checklist</p>
              <div className="space-y-1.5">
                {[
                  "Funnel builder confirmed to support UTM passthrough",
                  "All four UTM params are in the URL",
                  "utm_medium = paid or organic only",
                  "utm_source = platform or link+in+bio",
                  "utm_campaign = consistent ID for this campaign",
                  "utm_content = specific ad ID or content piece",
                  "Form configured to capture UTMs (FormSpark / Typeform / ClickFunnels)",
                  "If using Zapier → Sheets: UTM columns added + mapped in Zap",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <div className="mt-0.5 w-3.5 h-3.5 rounded border border-border flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Saved Campaigns ─────────────────────────────────────────── */}
      <div className="bg-card rounded-xl border border-border">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
            <ListIcon />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Saved UTM Links</h2>
            <p className="text-sm text-muted-foreground">
              {savedCampaigns.length} link{savedCampaigns.length !== 1 ? "s" : ""} saved
            </p>
          </div>
        </div>

        {loadingCampaigns ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : savedCampaigns.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">No UTM links saved yet. Build one above!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">Campaign</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">Medium</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">Source</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">Content</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">Generated URL</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">Saved</th>
                  <th className="text-center p-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {savedCampaigns.map((campaign, index) => (
                  <tr
                    key={campaign._id}
                    className={`border-b border-border ${index % 2 === 0 ? "bg-background" : "bg-muted/20"} hover:bg-muted/40 transition-colors`}
                  >
                    <td className="p-4">
                      <div className="font-medium text-sm">{campaign.campaignName}</div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold font-mono ${
                        campaign.utmMedium === "paid" ? "bg-blue-500/10 text-blue-400" : "bg-green-500/10 text-green-400"
                      }`}>
                        {campaign.utmMedium}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] rounded text-xs font-mono font-medium">
                        {campaign.utmSource}
                      </span>
                    </td>
                    <td className="p-4 text-xs text-muted-foreground font-mono">
                      {campaign.utmContent || "—"}
                    </td>
                    <td className="p-4 text-sm max-w-xs">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-muted-foreground text-xs font-mono">
                          {campaign.generatedUrl.length > 45
                            ? `${campaign.generatedUrl.slice(0, 45)}…`
                            : campaign.generatedUrl}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopyUrl(campaign.generatedUrl)}
                          className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                          title="Copy URL"
                        >
                          {copiedUrl === campaign.generatedUrl ? <CheckIcon /> : <CopyIcon />}
                        </button>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(campaign.createdAt)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(campaign)}
                          className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                          title="Edit"
                        >
                          <PencilIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(campaign)}
                          disabled={deletingId === campaign._id}
                          className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-muted-foreground hover:text-red-500 disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingId === campaign._id ? (
                            <div className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                          ) : (
                            <TrashIcon />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete UTM Link"
        itemName={campaignToDelete?.campaignName}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
